import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/AuthContext';
import { MessageSquare, Send, Trash2, User, Clock, Shield, Wrench } from 'lucide-react';

interface Note {
  id: string;
  auftrag_id: string;
  user_id: string | null;
  text: string;
  created_at: string;
}

interface UserInfo {
  name?: string;
  email: string;
  role?: 'admin' | 'monteur';
}

interface OrderNotesProps {
  auftragId: string;
}

const OrderNotes: React.FC<OrderNotesProps> = ({ auftragId }) => {
  const { user } = useAuth();
  const [notes, setNotes] = useState<Note[]>([]);
  const [newNote, setNewNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userInfo, setUserInfo] = useState<Record<string, UserInfo>>({});

  // Separate function to fetch user roles
  async function fetchUserRoles(userIds: string[]) {
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select(`
          user_id,
          role,
          monteure (
            name
          )
        `)
        .in('user_id', userIds);

      if (error) throw error;

      const infoMap: Record<string, UserInfo> = {};
      data?.forEach(role => {
        if (role.user_id) {
          infoMap[role.user_id] = {
            email: role.user_id,
            role: role.role,
            name: role.monteure?.name
          };
        }
      });

      setUserInfo(prev => ({
        ...prev,
        ...infoMap
      }));
    } catch (err) {
      console.error('Error fetching user roles:', err);
    }
  }

  useEffect(() => {
    loadNotes();
  }, [auftragId]);

  async function loadNotes() {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error } = await supabase
        .from('auftrag_notizen')
        .select('*')
        .eq('auftrag_id', auftragId)
        .order('created_at', { ascending: true });
      
      if (error) throw error;
      
      if (data) {
        setNotes(data);
        
        // Fetch user roles for all unique user IDs
        const userIds = [...new Set(data.map(note => note.user_id).filter(Boolean))];
        if (userIds.length > 0) {
          await fetchUserRoles(userIds);
        }
      }
    } catch (err: any) {
      console.error('Error loading notes:', err);
      setError('Fehler beim Laden der Notizen');
    } finally {
      setLoading(false);
    }
  }

  async function addNote() {
    if (!newNote.trim() || !user) return;
    
    try {
      setSubmitting(true);
      setError(null);
      
      const { data, error } = await supabase
        .from('auftrag_notizen')
        .insert([
          {
            auftrag_id: auftragId,
            user_id: user.id,
            text: newNote.trim()
          }
        ])
        .select('*')
        .single();
      
      if (error) throw error;
      
      if (data) {
        // Add the new note to the list
        setNotes(prev => [...prev, data]);
        
        // Fetch user role for the new note if needed
        if (data.user_id && !userInfo[data.user_id]) {
          await fetchUserRoles([data.user_id]);
        }
      }
      
      // Clear the input
      setNewNote('');
    } catch (err: any) {
      console.error('Error adding note:', err);
      setError('Fehler beim Hinzufügen der Notiz');
    } finally {
      setSubmitting(false);
    }
  }

  async function deleteNote(noteId: string) {
    if (!confirm('Möchten Sie diese Notiz wirklich löschen?')) {
      return;
    }
    
    try {
      setError(null);
      
      const { error } = await supabase
        .from('auftrag_notizen')
        .delete()
        .eq('id', noteId);
      
      if (error) throw error;
      
      // Remove the note from the list
      setNotes(prev => prev.filter(note => note.id !== noteId));
    } catch (err: any) {
      console.error('Error deleting note:', err);
      setError('Fehler beim Löschen der Notiz');
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getUserDisplay = (note: Note) => {
    if (!note.user_id || !userInfo[note.user_id]) {
      return {
        name: 'Unbekannt',
        role: null
      };
    }
    
    const info = userInfo[note.user_id];
    return {
      name: info.name || info.email,
      role: info.role
    };
  };

  const isOwnNote = (note: Note) => {
    return note.user_id === user?.id;
  };

  return (
    <div className="bg-white rounded-lg border p-4">
      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
        <MessageSquare className="text-blue-600" size={20} />
        Notizen und Nachrichten
      </h3>
      
      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md border border-red-200">
          {error}
        </div>
      )}
      
      <div className="mb-4 max-h-80 overflow-y-auto">
        {loading ? (
          <div className="text-center py-4 text-gray-500">Notizen werden geladen...</div>
        ) : notes.length === 0 ? (
          <div className="text-center py-4 text-gray-500">
            Keine Notizen vorhanden. Fügen Sie die erste Notiz hinzu!
          </div>
        ) : (
          <div className="space-y-3">
            {notes.map(note => {
              const userDisplay = getUserDisplay(note);
              return (
                <div 
                  key={note.id} 
                  className={`p-3 rounded-lg ${
                    isOwnNote(note) 
                      ? 'bg-blue-50 border border-blue-100' 
                      : 'bg-gray-50 border border-gray-200'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                      {userDisplay.role === 'admin' ? (
                        <Shield size={14} className="text-purple-600" />
                      ) : userDisplay.role === 'monteur' ? (
                        <Wrench size={14} className="text-blue-600" />
                      ) : (
                        <User size={14} />
                      )}
                      <span>{userDisplay.name}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        userDisplay.role === 'admin' 
                          ? 'bg-purple-100 text-purple-800'
                          : userDisplay.role === 'monteur'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {userDisplay.role === 'admin' ? 'Administrator' : 
                         userDisplay.role === 'monteur' ? 'Monteur' : 
                         'Benutzer'}
                      </span>
                    </div>
                    {isOwnNote(note) && (
                      <button
                        onClick={() => deleteNote(note.id)}
                        className="text-red-500 hover:text-red-700"
                        title="Notiz löschen"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                  <p className="text-gray-800 whitespace-pre-wrap">{note.text}</p>
                  <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                    <Clock size={12} />
                    <span>{formatDate(note.created_at)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      
      <div className="mt-4">
        <div className="flex items-center gap-2">
          <textarea
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder="Notiz hinzufügen..."
            className="flex-grow rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 min-h-[80px] resize-y"
            disabled={submitting}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                addNote();
              }
            }}
          />
          <button
            onClick={addNote}
            disabled={submitting || !newNote.trim()}
            className="bg-blue-600 text-white p-2 rounded-md hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed self-start"
            title="Notiz senden"
          >
            <Send size={20} />
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Drücken Sie Enter zum Senden oder Shift+Enter für einen Zeilenumbruch
        </p>
      </div>
    </div>
  );
};

export default OrderNotes;